import { EntityRelationAddon, EntityRelationAddonConstructor } from "./EntityRelationAddon";

export interface EntityRelationMetadata {
  entityType: string;
  displayName: string;
  dependencies: string[];
  requiredForEntities: string[];
}

export interface EntityRegistry {
  [entityType: string]: {
    addon: EntityRelationAddonConstructor;
    metadata: EntityRelationMetadata;
  };
}

export class EntityRelationsManager {
  private entityRegistry: EntityRegistry;
  private entityRelationAddons: EntityRelationAddon[];
  private currentEntityIndex: number = 0;
  private processedEntities: Record<string, any[]> = {};
  private uploadedData: any[];
  private onComplete: (processedData: Record<string, any[]>) => void;

  constructor(
    entityRegistry: EntityRegistry,
    entityRelationAddons: EntityRelationAddon[],
    uploadedData: any[],
    onComplete: (processedData: Record<string, any[]>) => void
  ) {
    this.entityRegistry = entityRegistry;
    this.entityRelationAddons = entityRelationAddons;
    this.uploadedData = uploadedData;
    this.onComplete = onComplete;
  }

  getResolvedAddonOrder(): EntityRelationAddon[] {
    const addons = [...this.entityRelationAddons];
    const resolvedOrder: EntityRelationAddon[] = [];
    const visited: Set<string> = new Set();

    const visit = (addon: EntityRelationAddon) => {
      if (visited.has(addon.entityType)) return;

      const dependencies = addon.metadata.dependencies || [];
      for (const depType of dependencies) {
        const depAddon = addons.find(a => a.entityType === depType);
        if (depAddon) {
          visit(depAddon);
        }
      }

      visited.add(addon.entityType);
      resolvedOrder.push(addon);
    };

    for (const addon of addons) {
      visit(addon);
    }

    return resolvedOrder;
  }

  startEntityResolution() {
    const resolvedAddons = this.getResolvedAddonOrder();
    this.entityRelationAddons = resolvedAddons;
    this.currentEntityIndex = 0;

    if (resolvedAddons.length > 0) {
      return resolvedAddons[0];
    }

    return null;
  }

  processEntityResult(entityType: string, result: any[]): EntityRelationAddon | null {
    this.processedEntities[entityType] = result;

    this.currentEntityIndex++;

    if (this.currentEntityIndex < this.entityRelationAddons.length) {
      return this.entityRelationAddons[this.currentEntityIndex];
    } else {
      this.onComplete(this.processedEntities);
      return null;
    }
  }

  getProcessedEntityData(entityType: string): any[] {
    return this.processedEntities[entityType] || [];
  }

  getAllProcessedEntities(): Record<string, any[]> {
    return this.processedEntities;
  }

  areEntityDependenciesResolved(entityType: string): boolean {
    const addon = this.entityRelationAddons.find(a => a.entityType === entityType);
    if (!addon) return false;

    const dependencies = addon.metadata.dependencies || [];
    return dependencies.every(dep => this.processedEntities[dep] !== undefined);
  }
}
